import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';

// Inquiry types
const inquiryTypes = [
    "Investment Services",
    "Software",
    "Consulting",
    "Research Access",
    "Community Programs",
    "Career Opportunities",
    "Media Inquiry",
    "Other"
];

export function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        message: '',
        phone: '',
        inquiryType: '',
        honeypot: '' // Honeypot field that should remain empty
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // If honeypot field is filled, silently reject but appear to accept
        if (formData.honeypot) {
            setIsSuccess(true);
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            // Simulate API call - in production, you would make an actual API call
            await new Promise(r => setTimeout(r, 1000));

            // Reset form and show success message
            setIsSuccess(true);
            setFormData({
                name: '',
                email: '',
                company: '',
                message: '',
                phone: '',
                inquiryType: '',
                honeypot: ''
            });
        } catch (err) {
            setError('Something went wrong. Please try again.');
            console.error('Form submission error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold text-green-700 mb-4">Message Sent!</h3>
                <p className="text-green-600 mb-6">
                    Thank you for contacting us. We'll get back to you shortly.
                </p>
                <Button
                    variant="outline"
                    onClick={() => setIsSuccess(false)}
                    className="border-green-300 text-green-700 hover:bg-green-100"
                >
                    Send Another Message
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field - hidden from users but visible to bots */}
            <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                <label htmlFor="honeypot">Leave this field empty</label>
                <input
                    type="text"
                    id="honeypot"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                />
            </div>

            {/* Visible form fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label htmlFor="name" className="block text-sm font-medium">
                        Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full p-3 border rounded-md"
                        value={formData.name}
                        onChange={handleChange}
                    />
                </div>

                <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium">
                        Email *
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full p-3 border rounded-md"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label htmlFor="company" className="block text-sm font-medium">
                        Company
                    </label>
                    <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full p-3 border rounded-md"
                        value={formData.company}
                        onChange={handleChange}
                    />
                </div>

                <div className="space-y-2">
                    <label htmlFor="phone" className="block text-sm font-medium">
                        Phone
                    </label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full p-3 border rounded-md"
                        value={formData.phone}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div className="space-y-2">
                <label htmlFor="inquiryType" className="block text-sm font-medium">
                    Inquiry Type *
                </label>
                <select
                    id="inquiryType"
                    name="inquiryType"
                    required
                    className="w-full p-3 border rounded-md"
                    value={formData.inquiryType}
                    onChange={handleChange}
                >
                    <option value="" disabled>Select an inquiry type</option>
                    {inquiryTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
            </div>

            <div className="space-y-2">
                <label htmlFor="message" className="block text-sm font-medium">
                    Message *
                </label>
                <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="w-full p-3 border rounded-md"
                    value={formData.message}
                    onChange={handleChange}
                />
            </div>

            {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-md">
                    {error}
                </div>
            )}

            <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto"
            >
                {isSubmitting ? (
                    <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                    </>
                ) : (
                    'Send Message'
                )}
            </Button>
        </form>
    );
} 